import { useEffect, useRef, useState } from 'react'

import type { SupportMessage } from '../types'

const NEW_REPLY_ANNOUNCEMENT = 'New reply from support'

// Long enough to be read out, short enough that a second reply announces again
// instead of repeating text a screen reader would treat as unchanged.
const ANNOUNCEMENT_LIFETIME_MS = 4_000

interface WatchedThread {
  ticketPublicId: string | null
  staffMessageId: number | null
}

const NOTHING_WATCHED: WatchedThread = {
  ticketPublicId: null,
  staffMessageId: null,
}

const newestStaffMessageId = (messages: SupportMessage[]): number | null =>
  messages.reduce<number | null>(
    (newest, message) =>
      message.authorType === 'staff' && (!newest || message.id > newest)
        ? message.id
        : newest,
    null,
  )

// What the thread's live region says: staff typing while it lasts, otherwise a
// reply that has just landed.
export const useThreadAnnouncement = (
  ticketPublicId: string,
  messages: SupportMessage[],
  typingLabel: string | null,
) => {
  const [hasNewReply, setHasNewReply] = useState(false)
  const watched = useRef(NOTHING_WATCHED)
  const staffMessageId = newestStaffMessageId(messages)

  useEffect(() => {
    if (watched.current.ticketPublicId !== ticketPublicId) {
      watched.current = { ticketPublicId, staffMessageId: null }
      setHasNewReply(false)
    }
    if (staffMessageId === null) return

    const announced = watched.current.staffMessageId
    watched.current = { ticketPublicId, staffMessageId }
    // The replies already in the thread when it opened are not news.
    if (announced !== null && staffMessageId > announced) setHasNewReply(true)
  }, [ticketPublicId, staffMessageId])

  useEffect(() => {
    if (!hasNewReply) return
    const timer = setTimeout(
      () => setHasNewReply(false),
      ANNOUNCEMENT_LIFETIME_MS,
    )
    return () => clearTimeout(timer)
  }, [hasNewReply])

  if (typingLabel) return typingLabel
  return hasNewReply ? NEW_REPLY_ANNOUNCEMENT : ''
}
